import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import AnimatedLogo from '@/components/ui/AnimatedLogo';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/Auth';
import { AUTH_ROUTES, getAuthRedirectUrl, mapAuthError } from '@/utils/auth';
import { useToast } from '@/hooks/useToast';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { resendVerificationEmail } = useAuth();
  const [emailInput, setEmailInput] = useState('');
  const [isResending, setIsResending] = useState(false);

  const email = searchParams.get('email');
  const targetEmail = useMemo(
    () => (email ? email.trim() : emailInput.trim()),
    [email, emailInput]
  );

  const handleResendVerification = async () => {
    if (!targetEmail || isResending) {
      return;
    }

    setIsResending(true);
    const error = await resendVerificationEmail(
      targetEmail,
      getAuthRedirectUrl(AUTH_ROUTES.login)
    );
    setIsResending(false);

    if (error) {
      const mappedError = mapAuthError(error, 'resend_verification');
      toast({
        title: t('login.toast.error.title.translation', 'Error'),
        description: t(mappedError.translationKey, mappedError.fallback),
      });
      return;
    }

    toast({
      title: t('login.toast.success.title.translation', 'Success'),
      description: t(
        'verify_email.toast.success.resend.translation',
        'Verification email sent. Please check your inbox.'
      ),
    });
  };

  return (
    <div className="min-h-screen-dynamic flex items-center justify-center p-4 relative bg-background">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl w-full">
        <div className="flex flex-row space-x-6 justify-center items-center">
          <AnimatedLogo size={90} />
          <Label className="text-5xl md:text-8xl text-foreground">dialup</Label>
        </div>
        <div className="flex flex-col w-full items-center justify-center md:items-start p-2">
          <div className="flex flex-col justify-center md:justify-start p-10 space-y-4">
            <h2 className="text-2xl font-bold text-foreground">
              {t(
                'login.verify_email.title.translation',
                'Check your email to verify your account'
              )}
            </h2>
            <p className="text-foreground">
              {t(
                'login.verify_email.description.translation',
                'We sent a verification link to your email address. Open that link, then come back and sign in.'
              )}
            </p>
            {email ? (
              <p className="text-sm text-muted-foreground">
                {t('login.verify_email.sent_to.translation', 'Sent to:')}{' '}
                {email}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t(
                  'verify_email.generic_description.translation',
                  'If you do not see your verification email, you can resend it below.'
                )}
              </p>
            )}
            {!email && (
              <Input
                id="verify-email-input"
                type="email"
                placeholder={t(
                  'verify_email.email_placeholder.translation',
                  'Enter your email'
                )}
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                className="!bg-card !text-foreground !placeholder:text-muted-foreground"
                disabled={isResending}
              />
            )}
            <Button
              type="button"
              variant="outline"
              className="justify-center mt-2 bg-card text-foreground"
              onClick={handleResendVerification}
              disabled={!targetEmail || isResending}
            >
              {isResending
                ? t('verify_email.resend.loading.translation', 'Sending...')
                : t(
                    'verify_email.resend.default.translation',
                    'Resend verification email'
                  )}
            </Button>
            <Button
              type="button"
              variant="gradient"
              className="justify-center mt-2 "
              onClick={() => navigate(AUTH_ROUTES.login)}
            >
              {t(
                'login.verify_email.back_to_login.translation',
                'Back to Login'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
